import React, { useEffect, useState, useRef } from 'react';
import { fetchApi, getBodaccRuns, triggerBodacc, reSearchAllBodacc, getBodaccProgress, getNdaTemplateInfo, uploadNdaTemplate,
         triggerBodaccProcedures, extendBodaccProcedures, getBodaccProceduresKpis,
         getBodaccProcedures, getBodaccProceduresExportUrl,
         getScoringStatus, recomputeAllScores, getConnectedUsers } from '../../api';

const ROLE_LABELS = {
  admin: 'Administrateur', analyste: 'Analyste', jury: 'Jury',
  startup: 'Startup', investisseur: 'Investisseur',
};
const ROLE_COLORS = {
  admin: '#1B1E38', analyste: '#6A1B9A', jury: '#1565C0',
  startup: '#2E7D32', investisseur: '#E67E22',
};

// 45 s -> "< 1 min", 754 s -> "12 min", 5520 s -> "1 h 32"
function fmtDuration(seconds) {
  const s = Math.max(0, seconds || 0);
  if (s < 60) return '< 1 min';
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h} h ${String(min % 60).padStart(2, '0')}`;
}

const PROC_LABELS = {
  cession: 'Cession',
  liquidation: 'Liquidation judiciaire',
  redressement: 'Redressement judiciaire',
};
const PROC_COLORS = {
  cession: '#1565C0',
  liquidation: '#C62828',
  redressement: '#E67E22',
};
import { useToast } from '../../components/Toast';

// SIRET APIs
const getSiretStats = () => fetchApi('/siret/stats');
const getMissingSiret = () => fetchApi('/siret/missing');
const searchSiret = (q) => fetchApi(`/siret/search?q=${encodeURIComponent(q)}`);
const assignSiret = (id, data) => fetchApi(`/siret/assign/${id}`, { method: 'PUT', body: JSON.stringify(data) });
const autoSearchAll = () => fetchApi('/siret/auto-search', { method: 'POST' });
const getNoLogo = () => fetchApi('/siret/no-logo');
const fixLogos = () => fetchApi('/siret/fix-logos', { method: 'POST' });
const updateAllLogos = () => fetchApi('/siret/update-all-logos', { method: 'POST' });
const setLogo = (id, logo_url) => fetchApi(`/siret/set-logo/${id}`, { method: 'POST', body: JSON.stringify({ logo_url }) });

// Geocode APIs
const getGeoStats = () => fetchApi('/geocode/stats');
const geocodeBatch = () => fetchApi('/geocode/batch', { method: 'POST' });
const geocodeAll = () => fetchApi('/geocode/all', { method: 'POST' });

// Re-ventilation SaaS
const getSaasReventilation = () => fetchApi('/admin/tools/saas-reventilation');

export default function AdminTools() {
  const toast = useToast();
  const [tab, setTab] = useState('siret');

  // SIRET state
  const [missing, setMissing] = useState([]);
  const [siretStats, setSiretStats] = useState(null);
  const [selectedStartup, setSelectedStartup] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [autoSearching, setAutoSearching] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Logos state
  const [noLogo, setNoLogo] = useState([]);
  const [fixingLogos, setFixingLogos] = useState(false);
  const [logoFixResult, setLogoFixResult] = useState(null);
  const [updatingAllLogos, setUpdatingAllLogos] = useState(false);
  const [updateAllResult, setUpdateAllResult] = useState(null);

  // Geocode state
  const [geoStats, setGeoStats] = useState(null);
  const [geoRunning, setGeoRunning] = useState(false);
  const [geoResult, setGeoResult] = useState(null);

  // Re-ventilation SaaS state
  const [reventil, setReventil] = useState(null);
  const [reventilLoading, setReventilLoading] = useState(false);

  // BODACC state
  const [bodaccRuns, setBodaccRuns] = useState([]);
  const [bodaccRunning, setBodaccRunning] = useState(false);
  const [bodaccProgress, setBodaccProgress] = useState(null);
  const bodaccIntervalRef = useRef(null);

  // BODACC Cessions / Liquidations / Redressements (recherche globale, indépendante des startups)
  const [cessionRuns, setCessionRuns] = useState([]);
  const [cessionRunning, setCessionRunning] = useState(false);
  const [cessionProgress, setCessionProgress] = useState(null);
  const [cessions, setCessions] = useState([]);
  const [procKpis, setProcKpis] = useState({ totals: {}, total: 0, oldest: null, newest: null });
  const [cessionLoading, setCessionLoading] = useState(false);
  const [cessionFilters, setCessionFilters] = useState({ q: '', procedure_type: 'all', period_from: '', period_to: '' });
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const cessionIntervalRef = useRef(null);

  // Scoring ISI state
  const [scoringStatus, setScoringStatus] = useState(null);
  const [scoringRunning, setScoringRunning] = useState(false);
  const [scoringResult, setScoringResult] = useState(null);

  // Utilisateurs connectés state
  const [connected, setConnected] = useState(null);
  const [connectedLoading, setConnectedLoading] = useState(false);
  const [connectedError, setConnectedError] = useState(null);

  // NDA template state
  const [ndaTemplate, setNdaTemplate] = useState(null);
  const [ndaUploading, setNdaUploading] = useState(false);
  const ndaFileRef = useRef(null);

  useEffect(() => { return () => {
    if (bodaccIntervalRef.current) clearInterval(bodaccIntervalRef.current);
    if (cessionIntervalRef.current) clearInterval(cessionIntervalRef.current);
  }; }, []);

  useEffect(() => {
    getSiretStats().then(setSiretStats).catch(console.error);
    getMissingSiret().then(setMissing).catch(console.error);
    getNoLogo().then(setNoLogo).catch(console.error);
    getGeoStats().then(setGeoStats).catch(console.error);
    getBodaccRuns().then(setBodaccRuns).catch(console.error);
    getNdaTemplateInfo().then(setNdaTemplate).catch(console.error);
  }, []);

  // ===== SIRET handlers =====
  const handleSearch = async (startup) => {
    setSelectedStartup(startup);
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await searchSiret(startup.name);
      setSearchResults(res.results || []);
    } catch (err) { toast.error(err.message); }
    setSearching(false);
  };

  const handleAssign = async (startup, match) => {
    const dirigeants = match.dirigeants.map(d => `${d.prenoms || ''} ${d.nom || ''} (${d.qualite || ''})`).join(', ');
    try {
      await assignSiret(startup.id, {
        siren: match.siren, siret: match.siret, adresse: match.adresse,
        dirigeants: dirigeants || null, date_creation: match.date_creation, nom_complet: match.nom_complet,
      });
      setMissing(m => m.filter(s => s.id !== startup.id));
      setSelectedStartup(null);
      setSearchResults([]);
    } catch (err) { toast.error(err.message); }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('isi_token');
      const resp = await fetch('/api/admin/exports/full-csv', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Erreur lors de l\'export (HTTP ' + resp.status + ')');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export_startups_complet.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Erreur export CSV : ' + err.message); }
    setExporting(false);
  };

  // ===== Logos handlers =====
  const handleFixLogos = async () => {
    setFixingLogos(true);
    try {
      const res = await fixLogos();
      setLogoFixResult(res);
      getNoLogo().then(setNoLogo);
    } catch (err) { toast.error(err.message); }
    setFixingLogos(false);
  };

  const handleUpdateAllLogos = async () => {
    const ok = await toast.confirm('Mettre à jour TOUS les logos ? Cette opération écrasera les logos existants avec les favicons actuels des sites web.');
    if (!ok) return;
    setUpdatingAllLogos(true); setUpdateAllResult(null);
    try {
      const res = await updateAllLogos();
      setUpdateAllResult(res);
      getNoLogo().then(setNoLogo);
    } catch (err) { toast.error(err.message); }
    setUpdatingAllLogos(false);
  };

  const handleSetLogoManual = async (startup) => {
    try {
      const url = new URL(startup.site_web);
      const domain = url.hostname.replace('www.', '');
      const logoUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
      await setLogo(startup.id, logoUrl);
      setNoLogo(l => l.filter(s => s.id !== startup.id));
    } catch (err) { toast.error('URL de site web invalide'); }
  };

  // ===== Geocode handlers =====
  const handleGeoBatch = async () => {
    setGeoRunning(true); setGeoResult(null);
    try {
      const r = await geocodeBatch();
      setGeoResult({ ...r, type: 'batch' });
      const poll = setInterval(() => {
        getGeoStats().then(s => {
          setGeoStats(s);
          if (s.geocodable === 0) { clearInterval(poll); setGeoRunning(false); }
        });
      }, 3000);
      setTimeout(() => { clearInterval(poll); setGeoRunning(false); getGeoStats().then(setGeoStats); }, 120000);
    } catch (err) { toast.error(err.message); setGeoRunning(false); }
  };

  const handleGeoAll = async () => {
    const ok = await toast.confirm('Re-géocoder toutes les startups avec adresse ? Cela écrasera les coordonnées existantes.');
    if (!ok) return;
    setGeoRunning(true); setGeoResult(null);
    try {
      const r = await geocodeAll();
      setGeoResult({ ...r, type: 'all' });
      const poll = setInterval(() => { getGeoStats().then(setGeoStats); }, 3000);
      setTimeout(() => { clearInterval(poll); setGeoRunning(false); getGeoStats().then(setGeoStats); }, 180000);
    } catch (err) { toast.error(err.message); setGeoRunning(false); }
  };

  // ===== BODACC handlers =====
  const pollBodaccProgress = (runId) => {
    bodaccIntervalRef.current = setInterval(async () => {
      try {
        const p = await getBodaccProgress(runId);
        setBodaccProgress(p);
        if (p.status === 'completed' || p.status === 'error') {
          clearInterval(bodaccIntervalRef.current);
          bodaccIntervalRef.current = null;
          setBodaccRunning(false);
          getBodaccRuns().then(setBodaccRuns);
        }
      } catch { /* ignore polling errors */ }
    }, 2000);
  };

  const handleBodaccTrigger = async () => {
    setBodaccRunning(true); setBodaccProgress(null);
    try {
      const result = await triggerBodacc();
      setBodaccProgress({ status: 'running', processed: 0, total_startups: result.total_startups, with_results: 0 });
      pollBodaccProgress(result.run_id);
    } catch (err) { toast.error('Erreur : ' + err.message); setBodaccRunning(false); }
  };

  const handleBodaccReSearchAll = async () => {
    setBodaccRunning(true); setBodaccProgress(null);
    try {
      const result = await reSearchAllBodacc();
      setBodaccProgress({ status: 'running', processed: 0, total_startups: result.total_with_siret, with_results: 0,
        skipped_no_siret: result.skipped_no_siret });
      pollBodaccProgress(result.run_id);
    } catch (err) { toast.error('Erreur : ' + err.message); setBodaccRunning(false); }
  };

  const bodaccLastRun = bodaccRuns[0];
  const bodaccPct = bodaccProgress && bodaccProgress.total_startups > 0
    ? Math.round((bodaccProgress.processed / bodaccProgress.total_startups) * 100) : 0;

  // ===== BODACC Cessions / Liquidations / Redressements handlers =====
  // Recherche directe sur l'API BODACC, indépendante des startups en base.
  const loadCessionRuns = () => getBodaccRuns('procedures').then(setCessionRuns).catch(console.error);
  // Récupère aussi les runs d'extension (procedures-extend) pour les fusionner.
  const loadAllProcRuns = async () => {
    try {
      const [a, b] = await Promise.all([
        getBodaccRuns('procedures'),
        getBodaccRuns('procedures-extend').catch(() => []),
      ]);
      const merged = [...(a || []), ...(b || [])]
        .sort((x, y) => new Date(y.started_at) - new Date(x.started_at));
      setCessionRuns(merged);
    } catch (e) { console.error(e); }
  };

  const loadKpis = () => getBodaccProceduresKpis().then(setProcKpis).catch(console.error);

  const loadCessions = async (overrideFilters) => {
    setCessionLoading(true);
    try {
      const f = overrideFilters || cessionFilters;
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await getBodaccProcedures(params);
      setCessions(r.results || []);
    } catch (e) { console.error(e); }
    finally { setCessionLoading(false); }
  };

  // Au montage de l'onglet : KPI + historique des batchs uniquement (pas la liste).
  useEffect(() => {
    if (tab === 'scoring') getScoringStatus().then(setScoringStatus).catch(console.error);
    if (tab === 'connected') loadConnected();
    if (tab === 'bodacc-cessions') { loadKpis(); loadAllProcRuns(); }
    if (tab === 'reventilation' && !reventil) {
      setReventilLoading(true);
      getSaasReventilation().then(setReventil).catch(console.error).finally(() => setReventilLoading(false));
    }
  }, [tab]);

  // ===== Scoring ISI handlers =====
  // Le calcul est synchrone cote serveur : sur un gros lot la requete peut
  // durer, d'ou l'etat "en cours" qui verrouille les deux boutons.
  const handleRecomputeScores = async (onlyStale) => {
    const n = onlyStale ? (scoringStatus?.to_refresh ?? 0) : (scoringStatus?.with_qit ?? 0);
    const ok = await toast.confirm(
      onlyStale
        ? `Recalculer les ${n} dossier(s) jamais scorés ou dont le QIT a changé depuis le dernier calcul ?`
        : `Recalculer le score des ${n} dossier(s) ayant des réponses QIT ?\n\n`
          + `Les scores existants seront remplacés par le résultat du calcul sur les réponses actuelles.`
    );
    if (!ok) return;
    setScoringRunning(true); setScoringResult(null);
    try {
      const res = await recomputeAllScores(onlyStale);
      setScoringResult(res);
      getScoringStatus().then(setScoringStatus);
      toast.success(`${res.updated} score(s) recalculé(s)`);
    } catch (err) { toast.error('Erreur : ' + err.message); }
    setScoringRunning(false);
  };

  // ===== Utilisateurs connectés =====
  // Pas de rafraîchissement automatique : la liste est rechargée à l'ouverture
  // de l'onglet et sur clic du bouton.
  const loadConnected = async () => {
    setConnectedLoading(true); setConnectedError(null);
    try {
      setConnected(await getConnectedUsers());
    } catch (err) { setConnectedError(err.message); }
    setConnectedLoading(false);
  };

  const handleReventilExport = async () => {
    try {
      const token = localStorage.getItem('isi_token');
      const resp = await fetch('/api/admin/tools/saas-reventilation/export', { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `reventilation_saas_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Export CSV téléchargé');
    } catch (err) { toast.error('Erreur export : ' + err.message); }
  };

  const pollCessionProgress = (runId) => {
    cessionIntervalRef.current = setInterval(async () => {
      try {
        const p = await getBodaccProgress(runId);
        setCessionProgress(p);
        if (p.status === 'completed' || p.status === 'error') {
          clearInterval(cessionIntervalRef.current); cessionIntervalRef.current = null;
          setCessionRunning(false);
          loadAllProcRuns(); loadKpis();
          // Recharge la liste seulement si l'utilisateur l'avait déjà ouverte via filtre.
          if (filtersApplied) loadCessions();
        }
      } catch { /* ignore */ }
    }, 2000);
  };

  const handleCessionTrigger = async () => {
    setCessionRunning(true); setCessionProgress(null);
    try {
      const result = await triggerBodaccProcedures();
      setCessionProgress({ status: 'running', processed: 0, total_startups: 0, with_results: 0 });
      pollCessionProgress(result.run_id);
    } catch (err) { toast.error('Erreur : ' + err.message); setCessionRunning(false); }
  };

  const handleCessionExtend = async () => {
    setCessionRunning(true); setCessionProgress(null);
    try {
      const result = await extendBodaccProcedures();
      if (result.before_date) {
        toast.success('Extension lancée — annonces antérieures au ' + result.before_date);
      }
      setCessionProgress({ status: 'running', processed: 0, total_startups: 0, with_results: 0 });
      pollCessionProgress(result.run_id);
    } catch (err) { toast.error('Erreur : ' + err.message); setCessionRunning(false); }
  };

  const handleApplyFilters = () => {
    setFiltersApplied(true);
    loadCessions();
  };

  const handleResetFilters = () => {
    const reset = { q: '', procedure_type: 'all', period_from: '', period_to: '' };
    setCessionFilters(reset);
    setFiltersApplied(false);
    setCessions([]);
  };

  const handleExportByType = async (type) => {
    const params = type && type !== 'all' ? { procedure_type: type } : {};
    try {
      const token = localStorage.getItem('isi_token');
      const resp = await fetch(getBodaccProceduresExportUrl(params), { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const suffix = type && type !== 'all' ? `_${type}` : '_toutes';
      a.download = `bodacc_procedures${suffix}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Export CSV téléchargé');
    } catch (err) { toast.error('Erreur export : ' + err.message); }
  };

  const handleCessionExport = async () => {
    const params = {};
    Object.entries(cessionFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const token = localStorage.getItem('isi_token');
      const resp = await fetch(getBodaccProceduresExportUrl(params), { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `bodacc_procedures_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { toast.error('Erreur export : ' + err.message); }
  };

  const cessionLastRun = cessionRuns[0];
  const cessionPct = cessionProgress && cessionProgress.total_startups > 0
    ? Math.round((cessionProgress.processed / cessionProgress.total_startups) * 100) : 0;

  const tabs = [
    { key: 'nda', label: 'Modèle NDA' },
    { key: 'siret', label: `SIREN & SIRET manquants (${missing.length})` },
    { key: 'logos', label: `Logos manquants (${noLogo.length})` },
    { key: 'geocode', label: `Géocodage (${geoStats?.geocodable || 0})` },
    { key: 'bodacc', label: `BODACC` },
    { key: 'bodacc-cessions', label: `BODACC Cession / Liquidation / Redressement` },
    { key: 'reventilation', label: 'Re-ventilation SaaS' },
    { key: 'scoring', label: 'Scoring ISI' },
    { key: 'connected', label: 'Utilisateurs connectés' },
  ];

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0 }}>Outils</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-light)' }}>Recherche de SIRET, de logo, de position géographique et d'annonce légale</p>
          </div>
          <button onClick={handleExportCSV} className="btn btn-accent" disabled={exporting}
            style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            {exporting ? (
              <>
                <div style={{ width: 14, height: 14, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Export en cours...
              </>
            ) : (
              <>Export CSV complet</>
            )}
          </button>
        </div>

        {/* Stats bar */}
        {siretStats && (
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            <div className="stat-card">
              <div className="stat-value">{siretStats.total}</div>
              <div className="stat-label">Startups dans l'annuaire</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#27AE60' }}>{siretStats.with_siren || 0}</div>
              <div className="stat-label">Avec SIREN valide</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#27AE60' }}>{siretStats.with_siret}</div>
              <div className="stat-label">Avec SIRET valide</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#E74C3C' }}>{noLogo.length}</div>
              <div className="stat-label">Logos manquants</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#E67E22' }}>{geoStats?.geocodable || 0}</div>
              <div className="stat-label">À géocoder</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="tab-bar">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '12px 24px', border: 'none', background: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                borderBottom: `3px solid ${tab === t.key ? 'var(--primary-light)' : 'transparent'}`,
                color: tab === t.key ? 'var(--primary)' : 'var(--text-light)', marginBottom: -2 }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ============ NDA TEMPLATE TAB ============ */}
        {tab === 'nda' && (
          <div>
            <p style={{ color: 'var(--text-light)', fontSize: 14, marginBottom: 24 }}>
              Gérez le modèle de NDA (accord de confidentialité) qui sera proposé au téléchargement aux startups participantes aux Trophées de l'Innovation.
            </p>

            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>📄</span> Modèle NDA actuel
              </h3>

              {ndaTemplate && ndaTemplate.exists ? (
                <div style={{ padding: 20, background: '#E8F5E9', borderRadius: 10, border: '1px solid #C8E6C9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <span style={{ fontSize: 28 }}>✅</span>
                        <strong style={{ fontSize: 16, color: '#2E7D32' }}>Un modèle NDA est configuré</strong>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: 13, color: '#555' }}>
                        <span style={{ fontWeight: 600 }}>Fichier :</span>
                        <span>{ndaTemplate.filename}</span>
                        <span style={{ fontWeight: 600 }}>Taille :</span>
                        <span>{(ndaTemplate.size / 1024).toFixed(1)} Ko</span>
                        <span style={{ fontWeight: 600 }}>Dernière mise à jour :</span>
                        <span>{ndaTemplate.last_modified ? new Date(ndaTemplate.last_modified).toLocaleString('fr-FR') : '-'}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <a href={`/api/nda/template`}
                        className="btn btn-outline" style={{ fontSize: 13, textDecoration: 'none', textAlign: 'center' }}
                        onClick={e => {
                          // Need auth header for download — use fetch instead
                          e.preventDefault();
                          const token = localStorage.getItem('isi_token');
                          fetch('/api/nda/template', { headers: { Authorization: `Bearer ${token}` } })
                            .then(r => {
                              if (!r.ok) throw new Error('Erreur téléchargement');
                              const cd = r.headers.get('content-disposition');
                              const name = cd ? cd.split('filename=')[1]?.replace(/"/g, '') : ndaTemplate.filename;
                              return r.blob().then(b => ({ b, name }));
                            })
                            .then(({ b, name }) => {
                              const url = URL.createObjectURL(b);
                              const a = document.createElement('a'); a.href = url; a.download = name; a.click();
                              URL.revokeObjectURL(url);
                            })
                            .catch(err => toast.error(err.message));
                        }}>
                        ⬇ Télécharger le modèle actuel
                      </a>
                      <button onClick={() => ndaFileRef.current?.click()}
                        className="btn btn-accent" style={{ fontSize: 13 }} disabled={ndaUploading}>
                        {ndaUploading ? 'Téléversement...' : '🔄 Remplacer le modèle'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: 24, background: '#FFF8E1', borderRadius: 10, border: '1px solid #FFE082', textAlign: 'center' }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
                  <strong style={{ fontSize: 16, color: '#F57F17' }}>Aucun modèle NDA configuré</strong>
                  <p style={{ color: 'var(--text-light)', margin: '8px 0 20px', fontSize: 14 }}>
                    Les startups ne pourront pas télécharger le NDA tant qu'un modèle n'aura pas été téléversé.
                  </p>
                  <button onClick={() => ndaFileRef.current?.click()}
                    className="btn btn-accent" style={{ fontSize: 14, padding: '12px 32px' }} disabled={ndaUploading}>
                    {ndaUploading ? 'Téléversement...' : '📤 Téléverser un modèle NDA'}
                  </button>
                </div>
              )}

              <input type="file" ref={ndaFileRef} style={{ display: 'none' }}
                accept=".docx,.doc,.pdf,.odt"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const ok = await toast.confirm(`Téléverser "${file.name}" (${(file.size / 1024).toFixed(0)} Ko) comme nouveau modèle NDA ?\n${ndaTemplate?.exists ? 'Le modèle actuel sera remplacé.' : ''}`);
                  if (!ok) { e.target.value = ''; return; }
                  setNdaUploading(true);
                  try {
                    await uploadNdaTemplate(file);
                    toast.success(`Modèle NDA "${file.name}" téléversé avec succès`);
                    const info = await getNdaTemplateInfo();
                    setNdaTemplate(info);
                  } catch (err) { toast.error('Erreur : ' + err.message); }
                  setNdaUploading(false);
                  e.target.value = '';
                }}
              />
            </div>

            <div className="card" style={{ fontSize: 13, color: 'var(--text-light)' }}>
              <h4 style={{ marginBottom: 8 }}>Fonctionnement</h4>
              <ul style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                <li>Le modèle NDA téléversé ici sera proposé en téléchargement aux startups participant aux <strong>Trophées de l'Innovation</strong></li>
                <li>La startup télécharge le modèle, le signe, puis le re-téléverse sur la plateforme (format PDF, JPEG ou PNG)</li>
                <li>L'analyste ou l'administrateur peut ensuite valider ou rejeter le NDA signé depuis l'espace <strong>Dossiers</strong></li>
                <li>Formats acceptés pour le modèle : <strong>.docx, .doc, .pdf, .odt</strong> (max 20 Mo)</li>
                <li>Le remplacement du modèle n'affecte pas les NDA déjà signés et téléversés par les startups</li>
              </ul>
            </div>
          </div>
        )}

        {/* ============ SIRET TAB ============ */}
        {tab === 'siret' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <p style={{ color: 'var(--text-light)', fontSize: 14, margin: 0 }}>
                {missing.length} startups sans SIREN ou SIRET valide.
                {siretStats?.siren_without_siret > 0 && <> Dont <strong style={{ color: '#E67E22' }}>{siretStats.siren_without_siret}</strong> avec SIREN mais sans SIRET.</>}
                {' '}Recherche via l'API du RCS (recherche-entreprises.gouv.fr).
              </p>
              <button onClick={async () => {
                const ok = await toast.confirm(`Lancer la recherche automatique pour ${missing.length} startups ?\nLes startups avec un seul résultat seront automatiquement complétées (SIRET, adresse, dirigeants).`);
                if (!ok) return;
                setAutoSearching(true); setAutoResult(null);
                try {
                  const result = await autoSearchAll();
                  setAutoResult(result);
                  getMissingSiret().then(setMissing);
                  getSiretStats().then(setSiretStats);
                } catch (err) { toast.error('Erreur : ' + err.message); }
                setAutoSearching(false);
              }} className="btn btn-accent" disabled={autoSearching || missing.length === 0}
                style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                {autoSearching ? 'Recherche en cours...' : `Recherche automatique (${missing.length})`}
              </button>
            </div>

            {autoSearching && (
              <div className="card" style={{ marginBottom: 16, background: '#FFF8E1', borderLeft: '4px solid #F57F17' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 20, height: 20, border: '3px solid #F57F17', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 14 }}>Recherche automatique en cours... Interrogation de l'API du RCS pour chaque startup.</span>
                </div>
              </div>
            )}

            {autoResult && !autoSearching && (
              <div className="card" style={{ marginBottom: 16, background: '#E8F5E9', borderLeft: '4px solid #27AE60' }}>
                <strong style={{ color: '#2E7D32' }}>Recherche automatique terminée</strong>
                <div style={{ marginTop: 8, display: 'flex', gap: 20, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>Recherchées : <strong>{autoResult.searched}</strong></span>
                  <span style={{ color: '#27AE60' }}>Assignées automatiquement : <strong>{autoResult.assigned}</strong></span>
                  {autoResult.siren_to_siret > 0 && <span style={{ color: '#2E75B6' }}>SIRET trouvés via SIREN : <strong>{autoResult.siren_to_siret}</strong></span>}
                  <span style={{ color: '#E67E22' }}>Résultats multiples : <strong>{autoResult.multiple}</strong> (à vérifier manuellement)</span>
                  <span style={{ color: '#999' }}>Non trouvées : <strong>{autoResult.skipped}</strong></span>
                  {autoResult.errors > 0 && <span style={{ color: '#E74C3C' }}>Erreurs : <strong>{autoResult.errors}</strong></span>}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gap: 6 }}>
              {missing.map(s => {
                const isOpen = selectedStartup?.id === s.id;
                const isSearching = isOpen && searching;
                return (
                  <div key={s.id} className="card" style={{ padding: 0, overflow: 'hidden',
                    border: isOpen ? '2px solid var(--primary-light)' : '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: 14 }}>{s.name}</strong>
                          {s.missing_siren && <span className="badge badge-rouge" style={{ fontSize: 9 }}>SIREN manquant</span>}
                          {s.missing_siret && !s.missing_siren && <span className="badge badge-orange" style={{ fontSize: 9 }}>SIRET manquant</span>}
                          {s.siren && <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-light)' }}>SIREN: {s.siren}</span>}
                          {s.site_web && <a href={s.site_web} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--primary-light)' }}>{s.site_web.replace('https://','').replace('http://','').substring(0, 30)}</a>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          {s.adresse && <span title={s.adresse}>{s.adresse.substring(0, 40)}{s.adresse.length > 40 ? '...' : ''}</span>}
                          {s.annee_creation && <span>{s.annee_creation}</span>}
                          {s.fondateurs && <span>{s.fondateurs.substring(0, 35)}{s.fondateurs.length > 35 ? '...' : ''}</span>}
                        </div>
                      </div>
                      <button onClick={() => {
                        if (isOpen) { setSelectedStartup(null); setSearchResults([]); }
                        else { handleSearch(s); }
                      }}
                        className={`btn ${isOpen ? 'btn-outline' : 'btn-primary'}`}
                        style={{ fontSize: 12, padding: '6px 16px', whiteSpace: 'nowrap', minWidth: 140, justifyContent: 'center' }}
                        disabled={isSearching}>
                        {isSearching ? 'Recherche...' : isOpen ? 'Fermer' : 'Rechercher SIRET'}
                      </button>
                    </div>

                    {isOpen && (
                      <div style={{ borderTop: '1px solid var(--border)', background: '#F8F9FF', padding: '12px 14px' }}>
                        {isSearching ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, justifyContent: 'center', color: 'var(--primary)' }}>
                            <div style={{ width: 16, height: 16, border: '2px solid var(--primary)', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                            Recherche en cours sur le RCS...
                          </div>
                        ) : searchResults.length === 0 ? (
                          <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
                            Aucun résultat trouvé pour "{s.name}"
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 4 }}>
                              {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''} — sélectionnez le bon :
                            </div>
                            {searchResults.map((r, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12,
                                border: '1px solid var(--border)', borderRadius: 8, background: 'white',
                                cursor: 'pointer', transition: 'all 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary-light)'}
                                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                    <strong style={{ fontSize: 14, color: 'var(--primary)' }}>{r.nom_complet}</strong>
                                    <span className={`badge ${r.etat_administratif === 'A' ? 'badge-vert' : 'badge-rouge'}`} style={{ fontSize: 9 }}>
                                      {r.etat_administratif === 'A' ? 'Active' : 'Fermée'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px 16px', fontSize: 12 }}>
                                    <div>
                                      <span style={{ color: 'var(--text-light)' }}>SIRET</span>
                                      <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.siret}</div>
                                    </div>
                                    <div>
                                      <span style={{ color: 'var(--text-light)' }}>SIREN</span>
                                      <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.siren}</div>
                                    </div>
                                    <div>
                                      <span style={{ color: 'var(--text-light)' }}>Création</span>
                                      <div style={{ fontWeight: 500 }}>{r.date_creation || '-'}</div>
                                    </div>
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <span style={{ color: 'var(--text-light)' }}>Adresse</span>
                                      <div style={{ fontWeight: 500 }}>{r.adresse || '-'}</div>
                                    </div>
                                    {r.dirigeants && r.dirigeants.length > 0 && (
                                      <div style={{ gridColumn: '1 / -1' }}>
                                        <span style={{ color: 'var(--text-light)' }}>Dirigeant(s)</span>
                                        <div style={{ fontWeight: 500 }}>
                                          {r.dirigeants.map(d => `${d.prenoms || ''} ${d.nom || ''} (${d.qualite || ''})`).join(', ')}
                                        </div>
                                      </div>
                                    )}
                                    <div>
                                      <span style={{ color: 'var(--text-light)' }}>NAF</span>
                                      <div style={{ fontWeight: 500 }}>{r.activite_principale || '-'}</div>
                                    </div>
                                  </div>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); handleAssign(s, r); }}
                                  className="btn btn-accent" style={{ fontSize: 12, padding: '8px 16px', whiteSpace: 'nowrap', alignSelf: 'center' }}>
                                  Valider
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {missing.length === 0 && (
                <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-light)' }}>
                  Toutes les startups ont un SIREN et un SIRET valides
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============ LOGOS TAB ============ */}
        {tab === 'logos' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <p style={{ color: 'var(--text-light)', fontSize: 14, margin: 0, flex: 1, minWidth: 200 }}>
                Startups avec un site web mais sans logo. Récupération automatique des favicons depuis les sites web.
              </p>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={handleFixLogos} className="btn btn-accent" disabled={fixingLogos || updatingAllLogos} style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                  {fixingLogos ? 'Récupération...' : `Récupérer les manquants (${noLogo.length})`}
                </button>
                <button onClick={handleUpdateAllLogos} className="btn btn-primary" disabled={updatingAllLogos || fixingLogos} style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                  {updatingAllLogos ? 'Mise à jour...' : 'Mettre à jour tous les logos'}
                </button>
              </div>
            </div>

            {logoFixResult && (
              <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid #27AE60', background: '#F1F8E9' }}>
                <strong style={{ color: '#2E7D32' }}>Logos récupérés !</strong>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  {logoFixResult.fixed} logos assignés sur {logoFixResult.total_without_logo} startups sans logo
                </div>
              </div>
            )}

            {updateAllResult && (
              <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid #2E75B6', background: '#E3F2FD' }}>
                <strong style={{ color: '#1565C0' }}>Mise à jour complète des logos terminée !</strong>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  {updateAllResult.updated} logos mis à jour sur {updateAllResult.total_with_website} startups avec site web
                  {updateAllResult.skipped > 0 && <span style={{ color: '#E67E22' }}> · {updateAllResult.skipped} ignorées (URL invalide)</span>}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gap: 6 }}>
              {noLogo.map(s => (
                <div key={s.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14 }}>
                  <div>
                    <strong>{s.name}</strong>
                    <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 2 }}>
                      <a href={s.site_web} target="_blank" rel="noopener noreferrer">{s.site_web}</a>
                    </div>
                  </div>
                  <button onClick={() => handleSetLogoManual(s)} className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px' }}>
                    Récupérer le logo
                  </button>
                </div>
              ))}
              {noLogo.length === 0 && (
                <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-light)' }}>
                  Toutes les startups avec un site web ont un logo
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============ GEOCODAGE TAB ============ */}
        {tab === 'geocode' && (
          <div>
            <p style={{ color: 'var(--text-light)', marginBottom: 24, fontSize: 14 }}>
              Transformation des adresses postales en coordonnées GPS (latitude/longitude) pour l'affichage sur les cartes interactives.
              Utilise l'API gratuite <a href="https://adresse.data.gouv.fr" target="_blank" rel="noopener noreferrer">adresse.data.gouv.fr</a>.
            </p>

            {geoStats && (
              <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card">
                  <div className="stat-value">{geoStats.total}</div>
                  <div className="stat-label">Startups total</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#27AE60' }}>{geoStats.with_coords}</div>
                  <div className="stat-label">Avec coordonnées GPS</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#2E75B6' }}>{geoStats.with_address}</div>
                  <div className="stat-label">Avec adresse postale</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#E67E22' }}>{geoStats.geocodable}</div>
                  <div className="stat-label">À géocoder</div>
                </div>
              </div>
            )}

            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Lancer le géocodage</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleGeoBatch} className="btn btn-accent" disabled={geoRunning || (geoStats && geoStats.geocodable === 0)}
                    style={{ fontSize: 13 }}>
                    {geoRunning ? 'En cours...' : `Géocoder les manquantes (${geoStats?.geocodable || 0})`}
                  </button>
                  <button onClick={handleGeoAll} className="btn btn-primary" disabled={geoRunning}
                    style={{ fontSize: 13 }}>
                    {geoRunning ? 'En cours...' : `Tout re-géocoder (${geoStats?.with_address || 0})`}
                  </button>
                </div>
              </div>

              {geoRunning && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{ width: 20, height: 20, border: '3px solid var(--accent)', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: 14 }}>Géocodage en cours via l'API adresse.data.gouv.fr...</span>
                  </div>
                  {geoStats && (
                    <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                      Progression : {geoStats.with_coords} / {geoStats.with_address} adresses géocodées
                    </div>
                  )}
                </div>
              )}

              {geoResult && !geoRunning && (
                <div style={{ padding: 16, background: '#E8F5E9', borderRadius: 8 }}>
                  <strong style={{ color: '#2E7D32' }}>Géocodage lancé !</strong>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    {geoResult.total_to_geocode} adresses à traiter. Le traitement s'effectue en arrière-plan.
                    Rechargez la page dans quelques instants pour voir les résultats.
                  </div>
                </div>
              )}

              {geoStats && geoStats.geocodable === 0 && !geoRunning && !geoResult && (
                <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 13, color: 'var(--text-light)' }}>
                  Toutes les startups avec une adresse postale ont déjà des coordonnées GPS.
                </div>
              )}
            </div>

            <div className="card" style={{ fontSize: 13, color: 'var(--text-light)' }}>
              <h4 style={{ marginBottom: 8 }}>Comment ça marche</h4>
              <ul style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                <li><strong>Géocoder les manquantes</strong> : traite uniquement les startups ayant une adresse mais pas encore de coordonnées GPS</li>
                <li><strong>Tout re-géocoder</strong> : re-traite toutes les startups avec adresse, y compris celles déjà géocodées</li>
                <li>L'API nationale utilise la <strong>Base Adresse Nationale (BAN)</strong> pour une précision maximale sur les adresses françaises</li>
                <li>Les coordonnées sont utilisées pour les <strong>cartes interactives</strong> (annuaire investisseur et espace jury)</li>
              </ul>
            </div>
          </div>
        )}

        {/* ============ SCORING ISI TAB ============ */}
        {tab === 'scoring' && (
          <div>
            <p style={{ color: 'var(--text-light)', marginBottom: 24, fontSize: 14 }}>
              Le score ISI est recalculé automatiquement à la soumission de la candidature.
              Il ne l'est pas quand un candidat modifie son questionnaire sans le soumettre,
              ni quand les pondérations sectorielles changent : ce recalcul groupé remet alors
              l'ensemble à jour, sans avoir à ouvrir chaque fiche dossier.
            </p>

            {scoringStatus && (
              <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card">
                  <div className="stat-value">{scoringStatus.with_qit}</div>
                  <div className="stat-label">Dossiers avec un QIT renseigné</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: scoringStatus.never_scored ? '#E67E22' : '#27AE60' }}>
                    {scoringStatus.never_scored}
                  </div>
                  <div className="stat-label">Jamais scorés</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: scoringStatus.stale ? '#E74C3C' : '#27AE60' }}>
                    {scoringStatus.stale}
                  </div>
                  <div className="stat-label">Scores périmés</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ fontSize: 18 }}>
                    {scoringStatus.last_run ? new Date(scoringStatus.last_run).toLocaleDateString('fr-FR') : '—'}
                  </div>
                  <div className="stat-label">Dernier calcul</div>
                </div>
              </div>
            )}

            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>Recalculer les scores</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleRecomputeScores(true)} className="btn btn-accent"
                    disabled={scoringRunning || !scoringStatus || scoringStatus.to_refresh === 0}
                    style={{ fontSize: 13 }}>
                    {scoringRunning ? 'En cours...' : `Recalculer les manquants et périmés (${scoringStatus?.to_refresh ?? 0})`}
                  </button>
                  <button onClick={() => handleRecomputeScores(false)} className="btn btn-primary"
                    disabled={scoringRunning || !scoringStatus || scoringStatus.with_qit === 0}
                    style={{ fontSize: 13 }}>
                    {scoringRunning ? 'En cours...' : `Recalculer tout (${scoringStatus?.with_qit ?? 0})`}
                  </button>
                </div>
              </div>

              {scoringRunning && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 20, height: 20, border: '3px solid var(--accent)',
                    borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 14 }}>
                    Recalcul en cours — ne quittez pas la page, le traitement est synchrone.
                  </span>
                </div>
              )}

              {scoringResult && !scoringRunning && (
                <div style={{ padding: 16, borderRadius: 8,
                  background: scoringResult.failed ? '#FFF8E1' : '#E8F5E9' }}>
                  <strong style={{ color: scoringResult.failed ? '#F57F17' : '#2E7D32' }}>
                    Recalcul terminé
                  </strong>
                  <div style={{ marginTop: 8, display: 'flex', gap: 20, fontSize: 13, flexWrap: 'wrap' }}>
                    <span>Dossiers traités : <strong>{scoringResult.candidates}</strong></span>
                    <span style={{ color: '#27AE60' }}>Recalculés : <strong>{scoringResult.updated}</strong></span>
                    {scoringResult.failed > 0 && (
                      <span style={{ color: '#E74C3C' }}>Échecs : <strong>{scoringResult.failed}</strong></span>
                    )}
                  </div>
                  {scoringResult.errors?.length > 0 && (
                    <ul style={{ marginTop: 10, marginBottom: 0, paddingLeft: 20, fontSize: 12.5, color: '#8D6E20' }}>
                      {scoringResult.errors.map(e => (
                        <li key={e.id}>{e.name} — {e.detail}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {scoringStatus && scoringStatus.to_refresh === 0 && !scoringRunning && !scoringResult && (
                <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 8,
                  fontSize: 13, color: 'var(--text-light)' }}>
                  Tous les dossiers ayant un QIT renseigné ont un score à jour.
                </div>
              )}
            </div>

            <div className="card" style={{ fontSize: 13, color: 'var(--text-light)' }}>
              <h4 style={{ marginBottom: 8 }}>Comment ça marche</h4>
              <ul style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                <li><strong>À la soumission</strong> : le score est calculé automatiquement, tout dossier soumis en a donc un</li>
                <li><strong>Recalculer les manquants et périmés</strong> : dossiers jamais scorés, ou dont le QIT a été modifié après le dernier calcul</li>
                <li><strong>Recalculer tout</strong> : reprend l'ensemble des dossiers ayant des réponses QIT — à utiliser après une modification des <strong>pondérations sectorielles</strong>, qui rend tous les scores obsolètes sans que le QIT ait bougé</li>
                <li>Un dossier en échec n'interrompt pas le lot : les erreurs sont listées à la fin</li>
                <li>Les scores alimentent la <strong>Matrice de classement</strong>, le <strong>classement du jury</strong> et le <strong>dashboard Trophées</strong></li>
              </ul>
            </div>
          </div>
        )}

        {/* ============ UTILISATEURS CONNECTÉS TAB ============ */}
        {tab === 'connected' && (() => {
          const rows = connected?.sessions || [];
          const distinctUsers = new Set(rows.map(r => r.user_id)).size;
          const byRole = rows.reduce((acc, r) => {
            acc[r.role] = acc[r.role] || new Set();
            acc[r.role].add(r.user_id);
            return acc;
          }, {});
          const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600,
            color: 'var(--text-light)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' };
          const td = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };
          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
                <p style={{ color: 'var(--text-light)', fontSize: 14, margin: 0, maxWidth: 720 }}>
                  Utilisateurs ayant eu une activité sur la plateforme au cours
                  des {connected?.idle_timeout_minutes ?? 5} dernières minutes.
                  La liste n'est pas actualisée en continu : cliquez sur le bouton pour la mettre à jour.
                </p>
                <button onClick={loadConnected} className="btn btn-accent" disabled={connectedLoading}
                  style={{ fontSize: 13, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {connectedLoading ? (
                    <>
                      <div style={{ width: 14, height: 14, border: '2px solid white', borderTop: '2px solid transparent',
                        borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                      Actualisation...
                    </>
                  ) : <>🔄 Utilisateurs connectés</>}
                </button>
              </div>

              {connectedError && (
                <div className="card" style={{ marginBottom: 16, background: '#FDECEA', borderLeft: '4px solid #E74C3C', fontSize: 13 }}>
                  {connectedError}
                </div>
              )}

              {connected && (
                <>
                  <div className="stats-grid" style={{ marginBottom: 16 }}>
                    <div className="stat-card">
                      <div className="stat-value" style={{ color: '#27AE60' }}>{distinctUsers}</div>
                      <div className="stat-label">Utilisateur{distinctUsers > 1 ? 's' : ''} connecté{distinctUsers > 1 ? 's' : ''}</div>
                    </div>
                    {Object.keys(ROLE_LABELS).filter(r => byRole[r]).map(r => (
                      <div key={r} className="stat-card">
                        <div className="stat-value" style={{ color: ROLE_COLORS[r] }}>{byRole[r].size}</div>
                        <div className="stat-label">{ROLE_LABELS[r]}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 8 }}>
                    Liste actualisée le {new Date(connected.generated_at).toLocaleDateString('fr-FR')} à{' '}
                    {new Date(connected.generated_at).toLocaleTimeString('fr-FR')}
                  </div>

                  <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
                    {rows.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)', fontSize: 14 }}>
                        Aucun utilisateur connecté
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={th}>Utilisateur</th>
                            <th style={th}>Rôle</th>
                            <th style={th}>Connexion</th>
                            <th style={th}>Temps de connexion</th>
                            <th style={th}>Dernière activité</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => {
                            const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
                            const started = new Date(r.started_at);
                            return (
                              <tr key={r.session_id}>
                                <td style={td}>
                                  <div style={{ fontWeight: 600 }}>{name || r.email}</div>
                                  {name && <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{r.email}</div>}
                                  {r.company && <div style={{ fontSize: 11, color: 'var(--text-light)' }}>{r.company}</div>}
                                </td>
                                <td style={td}>
                                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11,
                                    fontWeight: 600, color: 'white', background: ROLE_COLORS[r.role] || '#888', whiteSpace: 'nowrap' }}>
                                    {ROLE_LABELS[r.role] || r.role}
                                  </span>
                                </td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                  {started.toLocaleDateString('fr-FR')}{' '}
                                  <span style={{ color: 'var(--text-light)' }}>
                                    à {started.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </td>
                                <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtDuration(r.duration_seconds)}</td>
                                <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-light)' }}>
                                  {r.idle_seconds < 60 ? "à l'instant" : `il y a ${Math.floor(r.idle_seconds / 60)} min`}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}

              <div className="card" style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 24 }}>
                <h4 style={{ marginBottom: 8 }}>Comment ça marche</h4>
                <ul style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                  <li><strong>Connexion</strong> : début de la visite en cours. Après {connected?.idle_timeout_minutes ?? 5} minutes sans activité, la visite est considérée comme terminée ; le retour de l'utilisateur en ouvre une nouvelle</li>
                  <li><strong>Temps de connexion</strong> : durée écoulée depuis le début de la visite</li>
                  <li>Un onglet ouvert et visible signale la présence de l'utilisateur chaque minute, même s'il lit une page sans cliquer</li>
                  <li>Un utilisateur connecté depuis deux navigateurs apparaît sur deux lignes</li>
                  <li>La déconnexion via le bouton « Déconnexion » retire immédiatement l'utilisateur de la liste</li>
                </ul>
              </div>
            </div>
          );
        })()}

        {/* ============ RE-VENTILATION SAAS TAB ============ */}
        {tab === 'reventilation' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
              <p style={{ color: 'var(--text-light)', fontSize: 14, margin: 0, maxWidth: 720 }}>
                Requalification des startups « SaaS » (modèle horizontal) vers un secteur vertical, d'après leur nom, pitch et site web.
                <strong> Vue dérivée uniquement</strong> — la catégorie en base n'est pas modifiée. Exportez le résultat en CSV.
              </p>
              <button onClick={handleReventilExport} className="btn btn-accent" style={{ fontSize: 13, whiteSpace: 'nowrap' }} disabled={!reventil || reventil.total === 0}>
                Export CSV
              </button>
            </div>

            {reventilLoading && (
              <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 18, height: 18, border: '3px solid var(--primary)', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 14 }}>Traitement en cours…</span>
              </div>
            )}

            {reventil && !reventilLoading && (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                  <span className="badge" style={{ fontSize: 12, background: '#EEF1FB', color: 'var(--primary)' }}>{reventil.total} startups SaaS</span>
                  <span className="badge" style={{ fontSize: 12, background: '#E8F5E9', color: '#2E7D32' }}>{reventil.requalifies} requalifiées</span>
                  <span className="badge" style={{ fontSize: 12, background: '#EEF1FB', color: '#555' }}>{reventil.total - reventil.requalifies} conservées SaaS</span>
                  {Object.entries(reventil.summary).filter(([k]) => k !== 'SaaS').sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                    <span key={k} className="badge" style={{ fontSize: 12, background: '#F0F0F0', color: '#555' }}>{k} : {v}</span>
                  ))}
                </div>

                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#FAFAFA', borderBottom: '2px solid var(--border)' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>Nom</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>Catégorie renseignée</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>Catégorie requalifiée</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>Siret</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>Site Web</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reventil.items.map(it => {
                        const moved = it.categorie_requalifiee !== it.categorie;
                        return (
                          <tr key={it.id} style={{ borderBottom: '1px solid #F0F0F0', background: moved ? '#F1F8E9' : 'white' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 500 }}>{it.name}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--text-light)' }}>{it.categorie}</td>
                            <td style={{ padding: '8px 12px' }}>
                              {moved
                                ? <span className="badge badge-vert" style={{ fontSize: 11 }}>{it.categorie_requalifiee}</span>
                                : <span style={{ color: 'var(--text-light)' }}>SaaS</span>}
                            </td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11 }}>{it.siret || '—'}</td>
                            <td style={{ padding: '8px 12px' }}>
                              {it.site_web ? <a href={it.site_web} target="_blank" rel="noopener noreferrer">{it.site_web.replace(/^https?:\/\//, '').slice(0, 32)}</a> : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ============ BODACC TAB ============ */}
        {tab === 'bodacc' && (
          <div>
            <p style={{ color: 'var(--text-light)', marginBottom: 24, fontSize: 14 }}>
              Récupération des annonces du Bulletin Officiel des Annonces Civiles et Commerciales.
              La recherche se fait exclusivement par numéro de SIRET. Les startups sans SIRET sont ignorées.
            </p>

            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <h3 style={{ margin: 0 }}>Recherche BODACC</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleBodaccTrigger} className="btn btn-accent" disabled={bodaccRunning} style={{ fontSize: 13 }}>
                    {bodaccRunning ? 'En cours...' : 'Mise à jour incrémentale'}
                  </button>
                  <button onClick={handleBodaccReSearchAll} className="btn btn-primary" disabled={bodaccRunning} style={{ fontSize: 13 }}>
                    {bodaccRunning ? 'En cours...' : 'Rechercher toutes (avec SIRET)'}
                  </button>
                </div>
              </div>

              {bodaccRunning && bodaccProgress && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span>Progression : {bodaccProgress.processed || 0} / {bodaccProgress.total_startups} startups</span>
                    <span>{bodaccPct}%</span>
                  </div>
                  <div style={{ height: 10, background: '#F0F0F0', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{ width: `${bodaccPct}%`, height: '100%', background: 'linear-gradient(90deg, var(--primary), var(--accent))',
                      borderRadius: 5, transition: 'width 0.5s ease' }} />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-light)' }}>
                    {bodaccProgress.with_results || 0} startups avec annonces BODACC trouvées
                    {bodaccProgress.skipped_no_siret > 0 && ` · ${bodaccProgress.skipped_no_siret} ignorées (sans SIRET)`}
                  </div>
                </div>
              )}

              {bodaccProgress && bodaccProgress.status === 'completed' && !bodaccRunning && (
                <div style={{ padding: 16, background: '#E8F5E9', borderRadius: 8, fontSize: 14 }}>
                  <strong style={{ color: '#2E7D32' }}>Recherche terminée !</strong>
                  <div style={{ marginTop: 8, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <span>Startups analysées : <strong>{bodaccProgress.processed || bodaccProgress.total_startups}</strong></span>
                    <span>Avec BODACC : <strong style={{ color: '#1565C0' }}>{bodaccProgress.with_results}</strong></span>
                    {bodaccProgress.skipped_no_siret > 0 && <span style={{ color: '#E67E22' }}>Sans SIRET : <strong>{bodaccProgress.skipped_no_siret}</strong></span>}
                  </div>
                </div>
              )}

              {bodaccProgress && bodaccProgress.status === 'error' && !bodaccRunning && (
                <div style={{ padding: 16, background: '#FFEBEE', borderRadius: 8, fontSize: 14 }}>
                  <strong style={{ color: '#C62828' }}>Erreur lors de la recherche.</strong>
                  <div style={{ marginTop: 4 }}>Partiel : {bodaccProgress.processed || 0} traitées, {bodaccProgress.with_results || 0} avec résultats.</div>
                </div>
              )}

              {!bodaccProgress && bodaccLastRun && (
                <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-light)' }}>Dernière mise à jour : </span>
                  <strong>{bodaccLastRun.finished_at ? new Date(bodaccLastRun.finished_at).toLocaleString('fr-FR') : 'En cours'}</strong>
                  <span style={{ marginLeft: 16, color: 'var(--text-light)' }}>
                    {bodaccLastRun.with_results} avec annonces / {bodaccLastRun.total_startups} analysées
                  </span>
                </div>
              )}
            </div>

            {/* History */}
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16 }}>Historique des mises à jour</h3>
              {bodaccRuns.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>Aucune mise à jour effectuée</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      <th style={{ padding: 10, textAlign: 'left' }}>Date</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Statut</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Analysées</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Avec BODACC</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Taux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bodaccRuns.map(r => (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: 10 }}>{r.started_at ? new Date(r.started_at).toLocaleString('fr-FR') : '-'}</td>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          <span className={`badge ${r.status === 'completed' ? 'badge-vert' : r.status === 'error' ? 'badge-rouge' : 'badge-orange'}`} style={{ fontSize: 10 }}>
                            {r.status === 'completed' ? 'Terminé' : r.status === 'error' ? 'Erreur' : 'En cours'}
                          </span>
                        </td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 600 }}>{r.processed || r.total_startups}</td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: '#1565C0' }}>{r.with_results}</td>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          {r.total_startups ? `${Math.round((r.with_results / r.total_startups) * 100)}%` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="card" style={{ fontSize: 13, color: 'var(--text-light)' }}>
              <h4 style={{ marginBottom: 8 }}>À propos du BODACC</h4>
              <p>Le Bulletin Officiel des Annonces Civiles et Commerciales publie les actes enregistrés au RCS : créations, modifications, radiations, dépôts des comptes, procédures collectives. La recherche utilise le SIREN extrait du SIRET.</p>
              <p style={{ marginTop: 8 }}>Source : <a href="https://www.bodacc.fr" target="_blank" rel="noopener noreferrer">bodacc.fr</a> — API OpenDataSoft DILA</p>
            </div>
          </div>
        )}

        {/* ============ BODACC CESSIONS / LIQUIDATIONS / REDRESSEMENTS TAB ============ */}
        {/* Recherche directe sur l'API BODACC, INDÉPENDANTE des startups en base.
            Parcourt les annonces de la dernière parution jusqu'à 2 ans en arrière
            et conserve uniquement celles qui correspondent à une cession, une
            liquidation judiciaire ou un redressement judiciaire. */}
        {tab === 'bodacc-cessions' && (
          <div>
            <p style={{ color: 'var(--text-light)', marginBottom: 20, fontSize: 14 }}>
              Recherche <strong>générique</strong> sur l'API BODACC : à partir de la dernière parution et jusqu'à
              2 ans en arrière, on conserve uniquement les annonces correspondant à une <strong>cession</strong>,
              une <strong>liquidation judiciaire</strong> ou un <strong>redressement judiciaire</strong>.
              Cette fonction <em>n'utilise pas</em> les startups stockées dans la plateforme — elle interroge
              directement le BODACC et déduplique par identifiant d'annonce.
            </p>

            {/* Action card */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                <h3 style={{ margin: 0 }}>Batchs BODACC</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={handleCessionTrigger} className="btn btn-primary" disabled={cessionRunning} style={{ fontSize: 13 }}>
                    {cessionRunning ? 'En cours...' : 'Lancer un nouveau batch'}
                  </button>
                  <button onClick={handleCessionExtend} className="btn btn-accent" disabled={cessionRunning} style={{ fontSize: 13 }}
                    title="Récupère 10 000 annonces supplémentaires antérieures à la plus ancienne date déjà stockée">
                    {cessionRunning ? '...' : 'Étendre le batch (10 000 plus anciennes)'}
                  </button>
                </div>
              </div>

              {cessionRunning && cessionProgress && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span>
                      Annonces parcourues : {cessionProgress.processed || 0}
                      {cessionProgress.total_startups ? ` / ${cessionProgress.total_startups}` : ' (estimation en cours…)'}
                    </span>
                    <span>{cessionPct}%</span>
                  </div>
                  <div style={{ height: 10, background: '#F0F0F0', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{ width: `${cessionPct}%`, height: '100%', background: 'linear-gradient(90deg, var(--primary), var(--accent))',
                      borderRadius: 5, transition: 'width 0.5s ease' }} />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-light)' }}>
                    {cessionProgress.with_results || 0} annonces enregistrées (cessions, liquidations, redressements)
                  </div>
                </div>
              )}

              {cessionProgress && cessionProgress.status === 'completed' && !cessionRunning && (
                <div style={{ padding: 16, background: '#E8F5E9', borderRadius: 8, fontSize: 14 }}>
                  <strong style={{ color: '#2E7D32' }}>Batch terminé !</strong>
                  <div style={{ marginTop: 8, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <span>Annonces parcourues : <strong>{cessionProgress.processed || 0}</strong></span>
                    <span>Annonces enregistrées : <strong style={{ color: '#1565C0' }}>{cessionProgress.with_results || 0}</strong></span>
                  </div>
                </div>
              )}

              {cessionProgress && cessionProgress.status === 'error' && !cessionRunning && (
                <div style={{ padding: 16, background: '#FFEBEE', borderRadius: 8, fontSize: 14 }}>
                  <strong style={{ color: '#C62828' }}>Erreur lors du batch.</strong>
                </div>
              )}

              {!cessionProgress && cessionLastRun && (
                <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-light)' }}>Dernier batch : </span>
                  <strong>{cessionLastRun.finished_at ? new Date(cessionLastRun.finished_at).toLocaleString('fr-FR') : 'En cours'}</strong>
                  <span style={{ marginLeft: 16, color: 'var(--text-light)' }}>
                    {cessionLastRun.with_results || 0} enregistrées / {cessionLastRun.processed || 0} parcourues
                  </span>
                </div>
              )}
            </div>

            {/* KPI tableau de bord — toujours visibles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
              <div className="card" style={{ padding: 14, textAlign: 'center', borderTop: '4px solid var(--primary)' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>{procKpis.total || 0}</div>
                <div style={{ fontSize: 11, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
                  Total annonces stockées
                </div>
              </div>
              {['cession', 'liquidation', 'redressement'].map(t => (
                <div key={t} className="card" style={{ padding: 14, textAlign: 'center', borderTop: `4px solid ${PROC_COLORS[t]}` }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: PROC_COLORS[t] }}>{procKpis.totals?.[t] || 0}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
                    {PROC_LABELS[t]}
                  </div>
                </div>
              ))}
              <div className="card" style={{ padding: 14, textAlign: 'center', borderTop: '4px solid var(--text-light)' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                  {procKpis.oldest ? new Date(procKpis.oldest).toLocaleDateString('fr-FR') : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
                  Plus ancienne
                </div>
              </div>
              <div className="card" style={{ padding: 14, textAlign: 'center', borderTop: '4px solid var(--text-light)' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                  {procKpis.newest ? new Date(procKpis.newest).toLocaleDateString('fr-FR') : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
                  Plus récente
                </div>
              </div>
            </div>

            {/* Export rapide — toujours visible, indépendant des filtres */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 15 }}>Exporter les sociétés (CSV)</h3>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>
                    Choisissez le périmètre à exporter — toutes les sociétés trouvées sont stockées en base.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => handleExportByType('all')} className="btn btn-primary" style={{ fontSize: 12 }}
                    disabled={!procKpis.total}>
                    Toutes ({procKpis.total || 0})
                  </button>
                  <button onClick={() => handleExportByType('cession')}
                    style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                      border: `1px solid ${PROC_COLORS.cession}`, color: PROC_COLORS.cession, background: 'white',
                      borderRadius: 6, cursor: (procKpis.totals?.cession || 0) ? 'pointer' : 'not-allowed',
                      opacity: (procKpis.totals?.cession || 0) ? 1 : 0.5 }}
                    disabled={!procKpis.totals?.cession}>
                    Cessions ({procKpis.totals?.cession || 0})
                  </button>
                  <button onClick={() => handleExportByType('liquidation')}
                    style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                      border: `1px solid ${PROC_COLORS.liquidation}`, color: PROC_COLORS.liquidation, background: 'white',
                      borderRadius: 6, cursor: (procKpis.totals?.liquidation || 0) ? 'pointer' : 'not-allowed',
                      opacity: (procKpis.totals?.liquidation || 0) ? 1 : 0.5 }}
                    disabled={!procKpis.totals?.liquidation}>
                    Liquidations ({procKpis.totals?.liquidation || 0})
                  </button>
                  <button onClick={() => handleExportByType('redressement')}
                    style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                      border: `1px solid ${PROC_COLORS.redressement}`, color: PROC_COLORS.redressement, background: 'white',
                      borderRadius: 6, cursor: (procKpis.totals?.redressement || 0) ? 'pointer' : 'not-allowed',
                      opacity: (procKpis.totals?.redressement || 0) ? 1 : 0.5 }}
                    disabled={!procKpis.totals?.redressement}>
                    Redressements ({procKpis.totals?.redressement || 0})
                  </button>
                </div>
              </div>
            </div>

            {/* Filtres — l'utilisateur doit appliquer un filtre pour voir la liste */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 12 }}>
                Appliquez un filtre pour afficher les sociétés trouvées (par défaut, seuls les KPI sont affichés).
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-light)', marginBottom: 4, textTransform: 'uppercase' }}>Nom ou SIREN</label>
                  <input type="text" value={cessionFilters.q} placeholder="Dénomination ou SIREN..."
                    onChange={e => setCessionFilters({ ...cessionFilters, q: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') handleApplyFilters(); }}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-light)', marginBottom: 4, textTransform: 'uppercase' }}>Type</label>
                  <select value={cessionFilters.procedure_type}
                    onChange={e => setCessionFilters({ ...cessionFilters, procedure_type: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'white' }}>
                    <option value="all">Tous types</option>
                    <option value="cession">Cessions</option>
                    <option value="liquidation">Liquidations judiciaires</option>
                    <option value="redressement">Redressements judiciaires</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-light)', marginBottom: 4, textTransform: 'uppercase' }}>Date début</label>
                  <input type="date" value={cessionFilters.period_from}
                    onChange={e => setCessionFilters({ ...cessionFilters, period_from: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-light)', marginBottom: 4, textTransform: 'uppercase' }}>Date fin</label>
                  <input type="date" value={cessionFilters.period_to}
                    onChange={e => setCessionFilters({ ...cessionFilters, period_to: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleApplyFilters} className="btn btn-accent" style={{ fontSize: 13, flex: 1 }}>Filtrer</button>
                  <button onClick={handleResetFilters}
                    className="btn" style={{ fontSize: 13, background: 'var(--bg-secondary)' }}>Reset</button>
                </div>
              </div>
            </div>

            {/* Résultats : affichés uniquement après application d'un filtre */}
            {filtersApplied && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                    <strong style={{ color: 'var(--text)' }}>{cessions.length}</strong> annonce{cessions.length > 1 ? 's' : ''} affichée{cessions.length > 1 ? 's' : ''}
                    {cessions.length >= 1000 && <> (limite : 1000)</>}
                  </div>
                  <button onClick={handleCessionExport} className="btn btn-primary" style={{ fontSize: 13 }} disabled={cessions.length === 0}>
                    Exporter CSV
                  </button>
                </div>

                <div className="card" style={{ overflowX: 'auto', marginBottom: 20 }}>
                  {cessionLoading ? (
                    <p style={{ color: 'var(--text-light)', textAlign: 'center', padding: 24 }}>Chargement...</p>
                  ) : cessions.length === 0 ? (
                    <p style={{ color: 'var(--text-light)', textAlign: 'center', padding: 24 }}>
                      Aucune annonce ne correspond aux filtres.
                    </p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--bg-secondary)' }}>
                          <th style={{ padding: '10px 8px', textAlign: 'left' }}>Date</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left' }}>Type</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left' }}>Dénomination</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left' }}>SIREN</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left' }}>Annonce</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cessions.map(c => (
                          <React.Fragment key={c.id}>
                            <tr style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                              onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)}>
                              <td style={{ padding: '10px 8px' }}>{c.date_parution ? new Date(c.date_parution).toLocaleDateString('fr-FR') : '-'}</td>
                              <td style={{ padding: '10px 8px' }}>
                                <span style={{ background: PROC_COLORS[c.procedure_type] + '20', color: PROC_COLORS[c.procedure_type],
                                  padding: '3px 8px', borderRadius: 999, fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>
                                  {PROC_LABELS[c.procedure_type] || c.procedure_type}
                                </span>
                              </td>
                              <td style={{ padding: '10px 8px', fontWeight: 600 }}>{c.denomination || c.commercant || '-'}</td>
                              <td style={{ padding: '10px 8px', fontFamily: 'monospace' }}>{c.siren || '-'}</td>
                              <td style={{ padding: '10px 8px', color: 'var(--primary)', fontSize: 11 }}>
                                {expandedRow === c.id ? '▼ Masquer' : '▶ Voir'}
                              </td>
                            </tr>
                            {expandedRow === c.id && (
                              <tr style={{ background: '#FAFAFA' }}>
                                <td colSpan={5} style={{ padding: 16 }}>
                                  <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                                    {c.adresse && <div><strong>Adresse :</strong> {c.adresse}</div>}
                                    {c.commercant && <div><strong>Commerçant :</strong> {c.commercant}</div>}
                                    {c.famille_avis && <div><strong>Famille :</strong> {c.famille_avis}</div>}
                                    {c.description && (
                                      <div style={{ marginTop: 8, padding: 12, background: 'white', border: '1px solid var(--border)', borderRadius: 6 }}>
                                        <strong style={{ display: 'block', marginBottom: 4 }}>Contenu de l'annonce :</strong>
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{c.description}</span>
                                      </div>
                                    )}
                                    {c.annonce_id && (
                                      <div style={{ marginTop: 8 }}>
                                        <a href={`https://www.bodacc.fr/pages/annonces-detail/?q.id=id:${c.annonce_id}`}
                                          target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                                          ↗ Voir l'annonce sur bodacc.fr
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* Historique des batchs */}
            <div className="card">
              <h3 style={{ marginBottom: 16 }}>Historique des batchs BODACC</h3>
              {cessionRuns.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>Aucun batch effectué</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      <th style={{ padding: 10, textAlign: 'left' }}>Date</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Statut</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Parcourues</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Enregistrées</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Taux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cessionRuns.map(r => (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: 10 }}>{r.started_at ? new Date(r.started_at).toLocaleString('fr-FR') : '-'}</td>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          <span className={`badge ${r.status === 'completed' ? 'badge-vert' : r.status === 'error' ? 'badge-rouge' : 'badge-orange'}`} style={{ fontSize: 10 }}>
                            {r.status === 'completed' ? 'Terminé' : r.status === 'error' ? 'Erreur' : 'En cours'}
                          </span>
                        </td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 600 }}>{r.processed || 0}</td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: '#1565C0' }}>{r.with_results || 0}</td>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          {r.processed ? `${Math.round(((r.with_results || 0) / r.processed) * 100)}%` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
